import { isSSR } from '@atlaskit/editor-common/core-utils';
import type {
	BlockNodeIdentifiers,
	SyncBlockInstance,
} from '@atlaskit/editor-synced-block-provider';
import {
	batchFetchData,
	fetchReferences,
	getConfluencePageAri,
	parseResourceId,
} from '@atlaskit/editor-synced-block-provider';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { preloadMediaToken } from '@confluence/fabric-media-support/entry-points/preloadMediaToken.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';

import { addToSSREditorNodeDataProvidersStore } from './utils/ssrDataStore.ts';
import { syncBlockContainsNodes } from './utils/syncBlockContainsNodes.ts';
import { extractSyncBlockIdentifiers } from './utils/extractSyncBlockIdentifiers.ts';
import {
	SyncedBlocksSSRErrorCodeMap,
	type SyncedBlocksSSRErrorMetadata,
} from '@atlaskit/editor-common/sync-block';

const MEASURE_SEGMENT = 'ssr-app/prepare/preloadQuery/syncedBlocksData';

// Timeout for fetching synced block references in milliseconds
// This is currently set to higher value to account for potential network latencys
// We will monitor performance and adjust this value as needed
const DEFAULT_TIMEOUT = 1500;

export interface PreloadSyncedBlocksDataOptions {
	/**
	 * The ID of the content to preload synced blocks for
	 */
	contentId: string;

	/**
	 * The cloud ID associated with the content
	 */
	cloudId: string;

	/**
	 * Whether the content is a blog post
	 */
	isBlog: boolean;

	/**
	 * The ADF document containing synced block nodes
	 * Optional: If provided, uses bulk fetching (batchFetchData) for better performance
	 * If not provided, falls back to individual fetching (fetchReferences)
	 */
	doc?: JSONDocNode;
	/**
	 * The measure segment identifier for performance tracking
	 */
	measureSegment?: string;

	/**
	 * Timeout in milliseconds for fetching synced block references
	 * @default 1500
	 */
	timeout?: number;
}

/**
 * Preloads synced blocks data and stores it in the window object for hydration.
 *
 * This function handles a special case of editor node (syncBlock) data providers that requires
 * separate preloading due to its nested structure and performance characteristics.
 *
 * **Why separate preloading?**
 *
 * Synced blocks introduce additional network latency that can slow down the main
 * editor initialization if fetched together. By splitting the flows, we achieve
 * better parallelization:
 *
 * Instead of:
 * 1. Fetch main ADF + Synced blocks → Fetch other data providers combined (from both main ADF and Synced blocks)
 *
 * We do:
 * 1. Fetch main ADF → fetch main data providers
 * 2. Fetch synced blocks → fetch synced blocks' nested data providers
 *
 * This parallel execution ensures synced blocks don't block the main editor load,
 * this could help improve overall load times, and allow synced blocks to have a separate
 * timeout without affecting the main editor load.
 * However this means we might have more network requests in total due to less batching opportunities.
 *
 * **Feature flag encapsulation**
 *
 * All feature flag checks and conditional logic for synced blocks are centralized
 * here rather than scattered across multiple files.
 *
 * @param contentId - The ID of the content to preload synced blocks for
 * @param cloudId - The cloud ID associated with the content
 * @param isBlog - Whether the content is a blog post
 * @returns Promise resolving to SyncedBlocksData containing synced block references
 */
export async function preloadSyncedBlocksData(
	options: PreloadSyncedBlocksDataOptions,
): Promise<void> {
	const { contentId, cloudId, isBlog, doc, measureSegment, timeout } = options;
	// Check feature flags before attempting to fetch synced block data

	if (
		!contentId ||
		!cloudId ||
		!isSSR() ||
		typeof isBlog === 'undefined' ||
		!expVal('platform_synced_block', 'isEnabled', false)
	) {
		return;
	}

	const segmentName = measureSegment || MEASURE_SEGMENT;

	await SSRMeasures.run(segmentName, async () => {
		let syncBlockReferences: SyncBlockInstance[] | undefined;

		// If doc is provided, use bulk load (batchFetchData)
		// Otherwise, use the old method (fetchReferences)
		if (doc && fg('platform_synced_block_patch_3')) {
			// Extract block identifiers from the ADF document
			const blockNodeIdentifiers = extractSyncBlockIdentifiers(doc);

			// If no synced blocks found, return early
			if (blockNodeIdentifiers.length === 0) {
				return;
			}

			syncBlockReferences = await fetchSyncBlockReferencesBulk(
				contentId,
				cloudId,
				isBlog,
				blockNodeIdentifiers,
				segmentName,
				timeout,
			);

			// check if results match the requested block identifiers, if not,
			// it means the fetch was likely incomplete/failed,
			if (
				syncBlockReferences &&
				syncBlockReferences.length !== blockNodeIdentifiers.length &&
				fg('platform_synced_block_patch_3')
			) {
				// Find which block identifiers are missing from the results
				const returnedIdentifiers = new Set(
					syncBlockReferences.map(
						(ref) => `${ref.resourceId}:${ref.data?.blockInstanceId ?? 'unknown'}`,
					),
				);
				const missingIdentifiers = blockNodeIdentifiers.filter(
					(identifier) =>
						!returnedIdentifiers.has(`${identifier.resourceId}:${identifier.blockInstanceId}`),
				);

				ssrPrepareErrorsClient.submitError(
					new Error(
						`Synced block fetch returned incomplete results: ${missingIdentifiers.length} blocks missing`,
					),
					{
						code: SyncedBlocksSSRErrorCodeMap.FETCH_INCOMPLETE,
						contentId,
						cloudId,
						isBlog,
						identifiers: blockNodeIdentifiers,
						missingIdentifiers,
					} as SyncedBlocksSSRErrorMetadata,
				);
			}
		} else {
			// Fallback to fetching all references when doc is not available
			if (!fg('platform_synced_block_patch_4')) {
				syncBlockReferences = await fetchSyncBlockReferences(
					contentId,
					cloudId,
					isBlog,
					segmentName,
					timeout,
				);
			}
		}

		if (syncBlockReferences) {
			addToSSREditorNodeDataProvidersStore({ syncBlocks: syncBlockReferences });
		} else {
			return;
		}

		const tasks: Promise<any>[] = [];

		const syncedBlocksWithMedia = syncBlockReferences.filter((syncBlock) => {
			// Preload media data providers for synced blocks that contain media nodes
			const containedNodeTypes = syncBlockContainsNodes(syncBlock, ['media']);
			return containedNodeTypes.length > 0 && containedNodeTypes.includes('media');
		});

		syncedBlocksWithMedia.forEach((syncBlockInstance) => {
			tasks.push(preloadMediaTokensForSyncBlock(syncBlockInstance));
		});

		await Promise.allSettled(tasks);
	});
}

/**
 * Preload media tokens for synced blocks that contain media
 * Returns a map of resourceId -> media config for SSR
 */
export async function preloadMediaTokensForSyncBlock(
	syncBlock: SyncBlockInstance | undefined,
): Promise<Record<string, any> | null> {
	if (!syncBlock) {
		return null;
	}

	const { product, contentId } = parseResourceId(syncBlock.resourceId) || {};

	if (!product || !contentId) {
		return {
			resourceId: syncBlock.resourceId,
			result: undefined,
		};
	}

	if (product === 'confluence-page') {
		const result = await preloadMediaToken(contentId);

		return {
			resourceId: syncBlock.resourceId,
			result,
		};
	}

	return null;
}

/**
 * Fetches synced block references for a given content.
 * @param contentId - The ID of the content
 * @param cloudId - The cloud ID
 * @param isBlog - Whether the content is a blog post
 * @param timeout - Maximum time in milliseconds to wait (default: 800ms)
 * @returns Promise resolving to synced block instances or undefined on error/timeout
 */
export function fetchSyncBlockReferences(
	contentId: string,
	cloudId: string,
	isBlog: boolean,
	measureSegment: string = MEASURE_SEGMENT,
	timeout: number = DEFAULT_TIMEOUT,
): Promise<SyncBlockInstance[] | undefined> {
	return SSRMeasures.run(`${measureSegment}/fetch:SyncBlockReferences`, async () => {
		const pageAri = getConfluencePageAri({
			cloudId,
			pageId: contentId,
			pageType: isBlog ? 'blogpost' : 'page',
		});

		try {
			const results = await Promise.race([
				fetchReferences(pageAri),
				new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), timeout)),
			]);

			if (!results || typeof results === 'string') {
				// has error return undefined
				return undefined;
			}

			return results as SyncBlockInstance[];
		} catch {
			return undefined;
		}
	});
}

/**
 * Fetches synced block references for a given content using batch API (bulk load).
 * Used when the ADF document is available for extracting block identifiers.
 * @param contentId - The ID of the content
 * @param cloudId - The cloud ID
 * @param isBlog - Whether the content is a blog post
 * @param blockNodeIdentifiers - Array of block node identifiers to fetch
 * @param measureSegment - Segment name for performance tracking
 * @param timeout - Maximum time in milliseconds to wait (default: 1500ms)
 * @returns Promise resolving to synced block instances or undefined on error/timeout
 */
export function fetchSyncBlockReferencesBulk(
	contentId: string,
	cloudId: string,
	isBlog: boolean,
	blockNodeIdentifiers: BlockNodeIdentifiers[],
	measureSegment: string = MEASURE_SEGMENT,
	timeout: number = DEFAULT_TIMEOUT,
): Promise<SyncBlockInstance[] | undefined> {
	return SSRMeasures.run(`${measureSegment}/fetch:SyncBlockReferences`, async () => {
		const pageAri = getConfluencePageAri({
			cloudId,
			pageId: contentId,
			pageType: isBlog ? 'blogpost' : 'page',
		});

		const timeoutError = new Error('timeout');

		try {
			const results = await Promise.race([
				batchFetchData(cloudId, pageAri, blockNodeIdentifiers),
				new Promise<undefined>((resolve, reject) =>
					setTimeout(() => {
						if (fg('platform_synced_block_patch_3')) {
							reject(timeoutError);
						} else {
							resolve(undefined);
						}
					}, timeout),
				),
			]);

			if (!results) {
				// No results returned - this could indicate a failure in the batch fetch operation
				// this would not be timeout, as it will throw a timeout error instead,
				// so we can attribute this to a fetch failure and log accordingly
				if (fg('platform_synced_block_patch_3')) {
					ssrPrepareErrorsClient.submitError(new Error('Synced blocks fetch returned no results'), {
						code: SyncedBlocksSSRErrorCodeMap.BATCH_FETCH_FAILED,
						contentId,
						cloudId,
						isBlog,
						identifiers: blockNodeIdentifiers,
					} as SyncedBlocksSSRErrorMetadata);
				}
				return undefined;
			}

			return results;
		} catch (error) {
			if (fg('platform_synced_block_patch_3')) {
				if (error === timeoutError) {
					ssrPrepareErrorsClient.submitError(new Error('Synced blocks fetch operation timed out'), {
						code: SyncedBlocksSSRErrorCodeMap.FETCH_TIMEOUT,
						contentId,
						cloudId,
						isBlog,
						identifiers: blockNodeIdentifiers,
					} as SyncedBlocksSSRErrorMetadata);
				} else {
					// Network or API error
					const errorMessage = error instanceof Error ? error.message : String(error);
					const errorName = error instanceof Error ? error.name : undefined;

					ssrPrepareErrorsClient.submitError(
						new Error(
							`Batch fetch for synced blocks failed: unable to fetch ${blockNodeIdentifiers.length} blocks`,
						),
						{
							code: SyncedBlocksSSRErrorCodeMap.BATCH_FETCH_FAILED,
							contentId,
							cloudId,
							isBlog,
							identifiers: blockNodeIdentifiers,
							errorMessage,
							errorName,
						} as SyncedBlocksSSRErrorMetadata,
					);
				}
			}

			return undefined;
		}
	});
}
